import { type CSSProperties, useEffect, useState } from 'react';

import { ArrowLeft } from '@phosphor-icons/react';

import * as cssStyles from './MeteredPaywallSubscribeModal.css';
import type { MeteredPaywallSubscribeModalProps } from './MeteredPaywallSubscribeModal.types';

const BEEHIIV_TOS_URL = 'https://www.beehiiv.com/tou';
const BEEHIIV_PRIVACY_URL = 'https://www.beehiiv.com/privacy';

export function MeteredPaywallSubscribeModal({
  styles: paywallStyles = {},
  copy: paywallCopy = {},
  upgradePath = '/upgrade',
  homepagePath,
  premiumOfferId,
  referralId,
  loginPath,
  onSubscribe,
  isPreviewing = false,
  isInline = false,
}: MeteredPaywallSubscribeModalProps) {
  const {
    backgroundColor = '#ffffff',
    textColor = '#111827',
    shadow = '0 4px 24px rgba(0,0,0,0.12)',
    buttonBackgroundColor = '#8b5cf6',
    buttonTextColor = '#ffffff',
    inputBackgroundColor = '#ffffff',
    inputBorderColor = '#e5e7eb',
    placeholderTextColor = '#9ca3af',
    inputTextColor = '#111827',
    dividerColor = '#e5e7eb',
    showLogo = true,
    showBenefits = true,
    logoSize = 40,
    logoUrl,
    backButtonColor = '#6b7280',
  } = paywallStyles;

  const {
    title,
    description,
    email_placeholder_text,
    subscribe_button_text,
    upgrade_button_text,
    benefits_header,
    benefits = [],
    login_prompt,
    login_link_text,
    tosUrl,
    privacyPolicyUrl,
    showTosConsent = true,
    tos_agreement_prefix: tosAgreementPrefix = 'I agree to the',
    tos_text: tosText = 'Terms of Service',
    and_text: andText = 'and',
    privacy_policy_text: privacyPolicyText = 'Privacy Policy',
    or_divider_text: orDividerText = 'or',
  } = paywallCopy;

  useEffect(() => {
    if (isPreviewing || isInline) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isPreviewing, isInline]);

  const upgradeParams = new URLSearchParams();
  if (premiumOfferId) upgradeParams.set('offer_id', premiumOfferId);
  if (referralId) upgradeParams.set('ref', referralId);
  const upgradeUrl = upgradeParams.size > 0 ? `${upgradePath}?${upgradeParams.toString()}` : upgradePath;

  const [email, setEmail] = useState('');
  const [tosChecked, setTosChecked] = useState(isPreviewing);

  const isSubscribeDisabled = showTosConsent && !tosChecked;

  const hasBenefits = showBenefits && benefits.length > 0;

  const effectiveTosUrl = tosUrl || BEEHIIV_TOS_URL;
  const effectivePrivacyUrl = privacyPolicyUrl || BEEHIIV_PRIVACY_URL;

  const modal = (
    <div
      className={`flex flex-col gap-4 rounded-xl p-8 relative${isPreviewing ? ' pointer-events-none' : ''}`}
      style={{
        backgroundColor,
        color: textColor,
        boxShadow: shadow,
        width: 'min(512px, 100%)',
      }}
    >
      {showLogo && logoUrl && (
        <div className="flex justify-center mb-1">
          <img
            src={logoUrl}
            alt="Publication logo"
            style={{ height: logoSize, width: 'auto', borderRadius: logoSize * 0.15 }}
            className="object-contain"
          />
        </div>
      )}

      <div className="flex flex-col gap-1.5 text-center">
        <h2 className="m-0 text-[19.2px] font-semibold leading-snug" style={{ color: textColor }}>
          {title}
        </h2>
        <p className="m-0 text-sm opacity-70" style={{ color: textColor }}>
          {description}
        </p>
      </div>

      <div className="flex flex-col md:flex-row gap-2 items-stretch">
        <input
          type="email"
          placeholder={email_placeholder_text ?? undefined}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={`flex-1 min-w-0 border rounded-md px-3 py-2.5 text-sm outline-none box-border ${cssStyles.input}`}
          style={
            {
              backgroundColor: inputBackgroundColor,
              borderColor: inputBorderColor,
              color: inputTextColor,
              '--placeholder-color': placeholderTextColor,
            } as CSSProperties
          }
        />
        <button
          type="button"
          onClick={() => onSubscribe?.(email)}
          disabled={isSubscribeDisabled}
          className="border-none rounded-md px-4 py-2.5 text-sm font-semibold whitespace-nowrap w-full md:w-auto md:shrink-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          style={{ backgroundColor: buttonBackgroundColor, color: buttonTextColor }}
        >
          {subscribe_button_text}
        </button>
      </div>

      {showTosConsent && (
        <div className="flex items-center gap-2 text-xs" style={{ color: textColor }}>
          <input
            type="checkbox"
            name="tos_consent"
            className={`h-3.5 w-3.5 shrink-0 rounded border-gray-300${isPreviewing ? ' pointer-events-auto cursor-pointer' : ''}`}
            checked={tosChecked}
            onChange={(e) => setTosChecked(e.target.checked)}
          />
          <span>
            {`${tosAgreementPrefix} `}
            <a
              href={effectiveTosUrl}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-1 hover:opacity-80"
              style={{ color: 'inherit' }}
            >
              {tosText}
            </a>
            {` ${andText} `}
            <a
              href={effectivePrivacyUrl}
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-1 hover:opacity-80"
              style={{ color: 'inherit' }}
            >
              {privacyPolicyText}
            </a>
          </span>
        </div>
      )}

      <div className="flex items-center gap-2 text-sm opacity-50" style={{ color: textColor }}>
        <div className="flex-1 h-px" style={{ backgroundColor: dividerColor }} />
        {orDividerText}
        <div className="flex-1 h-px" style={{ backgroundColor: dividerColor }} />
      </div>

      <a
        href={upgradeUrl}
        className="w-full border-none rounded-md px-4 py-2.5 text-sm font-semibold cursor-pointer text-center no-underline"
        style={{ backgroundColor: buttonBackgroundColor, color: buttonTextColor }}
      >
        {upgrade_button_text}
      </a>

      {hasBenefits && (
        <div className="flex flex-col gap-2">
          <p className="m-0 text-[13px] font-semibold text-center" style={{ color: textColor }}>
            {benefits_header}
          </p>
          <div className="flex justify-center">
            <ul className="m-0 list-disc pl-[18px] flex flex-col gap-1">
              {benefits.map((benefit) => (
                <li key={benefit} className="text-[13px] opacity-80" style={{ color: textColor }}>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <p className="m-0 text-[13px] text-center opacity-60" style={{ color: textColor }}>
        {login_prompt}{' '}
        <a href={loginPath} className="underline" style={{ color: textColor }}>
          {login_link_text}
        </a>
      </p>

      <a
        href={homepagePath ?? '/'}
        className="absolute top-4 left-4 bg-transparent border-none cursor-pointer p-1 flex items-center justify-center"
        style={{ color: backButtonColor }}
        aria-label="Go back"
      >
        <ArrowLeft size={16} />
      </a>
    </div>
  );

  if (isPreviewing || isInline) {
    return <div className="flex justify-center px-4 w-full">{modal}</div>;
  }

  return <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/60">{modal}</div>;
}
